import {
  Component,
  ViewChild,
  OnInit,
  AfterViewInit,
  HostListener,
} from '@angular/core';
import { AppService } from '@app/app.service';
import { AuthService } from '@app/auth/auth.service';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { LoadDialogComponent } from '@app/loads/load-dialog/load-dialog.component';
import { RouterModule } from '@angular/router';
import { DatePipe, CommonModule } from '@angular/common';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatChip } from '@angular/material/chips';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

@Component({
  selector: 'app-loads',
  imports: [
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    RouterModule,
    DatePipe,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    CommonModule,
    MatChip,
    MatMenuModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './loads.component.html',
  styleUrl: './loads.component.scss',
  providers: [DatePipe],
})
export class LoadsComponent implements OnInit, AfterViewInit {

  isMobile = window.innerWidth < 768;

  itemsPerPage = this.isMobile ? 10 : 25;

  desktopColumns = [
    'age',
    'origin_start_date',
    'origin_city',
    'origin_state',
    'destination_start_date',
    'destination_city',
    'destination_state',
    'rate',
    'weight',
    'dimensions',
    'well_length',
    'visible',
    'actions',
  ];

  mobileColumns = [
    'mobile'
  ];

  displayedColumns = this.isMobile ? this.mobileColumns : this.desktopColumns;

  @HostListener('window:resize')
  onResize(): void {
    this.isMobile = window.innerWidth < 768;
    this.displayedColumns = this.isMobile
      ? this.mobileColumns
      : this.desktopColumns;
  }

  dataSource = new MatTableDataSource<any>([]);

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  loading = false;

  constructor(
    private router: Router,
    private appService: AppService,
    private authService: AuthService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    if (!this.authService.userCanPostLoads()) {
      this.appService.navigateToDefaultUrl();
      return;
    }

    this.getMyLoads();
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
  }

  getMyLoads(successMessage: boolean = false): void {
    this.loading = true;
    this.appService.getMyLoads().subscribe({
      next: (success) => {
        this.dataSource.data = success.data || [];

        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.paginator;
        if (successMessage) {
          this.appService.showSnackBar(success.message, 'success');
        }
        this.loading = false;
      },
      error: (err) => {
        this.dataSource.data = [];
        this.appService.handleError(err);
        this.loading = false;
      },
    });
  }

  createLoad(): void {
    this.openLoadDialog();
  }

  updateLoad(load: any): void {
    this.openLoadDialog(load);
  }

  copyLoad(load: any): void {
    this.openLoadDialog(load, true);
  }

  openLoadDialog(load: any | null = null, copy: boolean = false): void {
    let mode = 'add';

    if (load) {
      mode = copy ? 'add' : 'edit';
    }

    const dialogRef = this.dialog.open(LoadDialogComponent, {
      width: '750px',
      maxWidth: '750px',
      data: {
        load: load ? load : null,
        mode: mode,
        display: 'dialog',
        copy: copy,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.getMyLoads();
      }
    });
  }

  refreshLoad(load: any): void {
    const loadRefreshLimit = 30;
    const now = new Date();
    const lastRefreshed = new Date(load.refreshed_at);

    const timeDiff = Math.round(
      (now.getTime() - lastRefreshed.getTime()) / (1000 * 60)
    );

    const refreshIn = loadRefreshLimit - timeDiff;

    if (timeDiff < loadRefreshLimit) {
      this.appService.showSnackBar(
        `You can refresh this load in ${refreshIn} minutes.`,
        'warning'
      );
      return;
    }

    this.appService.refreshLoad(load.id).subscribe({
      next: (success) => {
        this.appService.showSnackBar(success.message, 'success');
        this.getMyLoads();
      },
      error: (err) => {
        this.appService.handleError(err);
      },
    });
  }

  toggleVisibility(load: any): void {
    const originalVisible = load.visible;
    const loadId = load.id;
    const newVisible = !originalVisible;

    this.updateLocalVisibility(loadId, newVisible);

    this.appService.toggleVisibility(loadId).subscribe({
      next: (response) => {
        this.appService.showSnackBar(response.message, 'success');
      },
      error: (err) => {
        this.updateLocalVisibility(loadId, originalVisible);
        this.appService.handleError(err);
      },
    });
  }

  private updateLocalVisibility(loadId: number, newVisible: boolean): void {
    const updated = this.dataSource.data.map((load: any) =>
      load.id === loadId ? { ...load, visible: newVisible } : load
    );

    this.dataSource.data = updated;
  }

  deleteLoad(load: any): void {
    this.appService
      .confirm('Are you sure you want to delete this load?')
      .subscribe((result) => {
        if (!result) return;

        this.appService.delete(`loads/${load.id}`).subscribe({
          next: (response) => {
            this.appService.showSnackBar(response.message, 'success');

            this.dataSource.data = this.dataSource.data.filter(
              (item: any) => item.id !== load.id
            );
          },
          error: (err) => {
            this.appService.handleError(err);
          },
        });
      });
  }

  matchTruck(load: any): void {
    this.router.navigate(['/trucks/searches'], {
      state: { loadToMatch: load },
    });
  }

  formatAge(refreshedSince: string): string {
    if (!refreshedSince) {
      return '';
    }

    const [hours, minutes] = refreshedSince.split(':').map(Number);

    if (hours < 24) {
      return refreshedSince;
    }

    const days = Math.floor(hours / 24);

    return `${days} day${days > 1 ? 's' : ''}`;
  }

  getDimensions(load: any): string {
    const length = this.toFeetAndInches(load.length);
    const width = this.toFeetAndInches(load.width);
    const height = this.toFeetAndInches(load.height);

    const dimensions = [length, width, height]
      .filter((dim) => dim)
      .join(' × ');

    return dimensions;
  }

  toFeetAndInches(totalInches: number): string {
    const feet = Math.floor(totalInches / 12);
    const inches = totalInches % 12;

    return `${feet}-${inches}`;
  }

  isFresh(item: any): boolean {
    if (!item.refreshed_since) {
      return false;
    }

    const [hours, minutes] = item.refreshed_since.split(':').map(Number);

    return hours === 0 && minutes < 5;
  }

  isVisible(load: any): boolean {
    return load.visible;
  }

}